import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Advent of Code Day 15 – Dueling JavaScript Generators",
  "description": "",
  "published": "2017-12-15T00:00:00.000Z",
  "redirect_from": ["/blog/advent-code-day-15-dueling-javascript-generators/swizec/7973"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "mM5iTlp-Pe4",
      "videostartat": "0"
    }}></lite-youtube>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/carbon-generator.png",
        "alt": "JavaScript generators are 👌"
      }}></img></p>
    <p>{`Advent of Code Day 15 was basically all about finding the right programming language. It needs built-in support for generators. You don't want to build that from scratch.`}</p>
    <p>{`But what's a generator anyway?`}</p>
    <p>{`Well, a generator is like an infinite loop that doesn't cause problems. The computer holds it paused in memory and only advances to the next iteration when you say, `}<em parentName="p">{`"Hey, what's the next value?"`}</em></p>
    <p>{`I don't know how it's implemented internally. If I had to guess, I'd say the engine holds the entire state on a stack of some sort and advances values when you ask for the next step. A lot like recursion.`}</p>
    <p>{`The full generator for Day 15 looks like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function* star2generator(factor, startingValue, denominator) {
  let val = startingValue;
  while (true) {
    val = (val * factor) % 2147483647;
    if (val % denominator === 0) {
      yield val;
    }
  }
}
`}</code></pre>
    <p>{`This starts from a `}<inlineCode parentName="p">{`startingValue`}</inlineCode>{` and multiplies `}<inlineCode parentName="p">{`val`}</inlineCode>{` with `}<inlineCode parentName="p">{`factor`}</inlineCode>{` and mods the result with `}<inlineCode parentName="p">{`2147483647`}</inlineCode>{` `}<em parentName="p">{`forever`}</em>{`. Notice the `}<inlineCode parentName="p">{`while(true)`}</inlineCode>{`? When I say forever, I mean `}<em parentName="p">{`forever`}</em>{`.`}</p>
    <p>{`But it won't crash your node runtime or your browser. Because `}<inlineCode parentName="p">{`function*`}</inlineCode>{` and `}<inlineCode parentName="p">{`yield`}</inlineCode>{` turn it into a generator that follows the "only execute when called" logic.`}</p>
    <p>{`You get the next value like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const A = generator(16807, startA, denomA);
A.next().value; // some number
A.next().value; // next number
`}</code></pre>
    <p>{`The full generator only returns values that are divisible by a `}<inlineCode parentName="p">{`denominator`}</inlineCode>{`. That's from part 2 of Day 15. The first part returned every value.`}</p>
    <p>{`If that `}<inlineCode parentName="p">{`2147483647`}</inlineCode>{` number looks familiar, it's because that's the highest number you can represent in 32 bits. This is significant because we're going to be doing some bitwise operations and JavaScript can only do those up to 32 bits.`}</p>
    <p>{`Numbers are generally represented as 64 bits in JavaScript.`}</p>
    <h2 {...{
      "id": "wtf-are-we-doing"
    }}>{`Wtf are we doing?`}</h2>
    <p>{`Right, so that's how generators work. The reason we need generators is that the puzzle for Advent of Code Day 15 goes like this`}</p>
    <blockquote>
      <p parentName="blockquote">{`Take two generators that follow similar logic and see how often the lowest 16 bits of their output match in 40,000,000 tries.`}</p>
      <p parentName="blockquote">{`For Star 2 we make them sync up a little better, check for only 5,000,000 tries`}</p>
    </blockquote>
    <p>{`The generator above can handle both puzzle generators. They only differ in starting value and denominator.`}</p>
    <p>{`To count the number of matches, I used this function 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function countMatches({
  startA,
  startB,
  generator,
  denominators = [],
  sampleSize,
}) {
  const A = generator(16807, startA, denominators[0]),
    B = generator(48271, startB, denominators[1]);

  let judge = 0,
    mask = 0b00000000000000001111111111111111;

  for (let i = 0; i < sampleSize; i++) {
    if ((A.next().value & mask) === (B.next().value & mask)) {
      judge += 1;
    }
  }

  return judge;
}
`}</code></pre>
    <p>{`The same function can do both Star 1 and Star 2 of the puzzle depending on input.`}</p>
    <p>{`Here's how it works:`}</p>
    <ol>
      <li parentName="ol">{`Create generators `}<inlineCode parentName="li">{`A`}</inlineCode>{` and `}<inlineCode parentName="li">{`B`}</inlineCode></li>
      <li parentName="ol">{`Start at count of `}<inlineCode parentName="li">{`0`}</inlineCode>{` (the `}<inlineCode parentName="li">{`judge`}</inlineCode>{` variable)`}</li>
      <li parentName="ol">{`Create a mask that takes out the lower 16 bits of a number`}</li>
      <li parentName="ol">{`Loop until `}<inlineCode parentName="li">{`sampleSize`}</inlineCode></li>
      <li parentName="ol">{`Take `}<inlineCode parentName="li">{`next()`}</inlineCode>{` values from the generators, bitwise `}<inlineCode parentName="li">{`AND`}</inlineCode>{` them with the mask`}<a parentName="li" {...{
          "href": "#fn1"
        }}>{`1`}</a>{`, compare values`}</li>
      <li parentName="ol">{`If numbers match, increment `}<inlineCode parentName="li">{`judge`}</inlineCode></li>
      <li parentName="ol">{`When it's done looping, `}<inlineCode parentName="li">{`judge`}</inlineCode>{` is our result`}</li>
    </ol>
    <p>{`Solving for Star 1 and Star 2 then becomes a matter of calling the `}<inlineCode parentName="p">{`matchCount`}</inlineCode>{` function with our inputs.`}</p>
    <pre><code parentName="pre" {...{}}>{`console.log(
    countMatches({
        startA: 591,
        startB: 393,
        generator: star2generator,
        denominators: [4, 8],
        sampleSize: 5000000
    })
);
`}</code></pre>
    <p>{`Boom 💥 puzzle solved in about 20 minutes. 🤙`}</p>
    <p>{`And now you know how generators work. Although I still don't know where you'd use them in a web app.`}</p>
    <ol>
      <li parentName="ol">{`If you're not used to bitwise operations, they're rare in JavaScript, the idea is that bits follow a truth table. 0 & 0 == 0, 0 & 1 == 0, 1 & 0 == 0, 1 & 1 == 1. You can use this to cut away parts of numbers with a `}<inlineCode parentName="li">{`mask`}</inlineCode>{`. For example 0b1010 & 0b0011 == 0b0010. 🤓 `}<a parentName="li" {...{
          "href": "#ffn1"
        }}>{`↩`}</a></li>
    </ol>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      